import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FormControl, InputLabel, OutlinedInput } from '@mui/material';
import { differenceInYears, isValid, parseISO } from 'date-fns';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { createIMaskInput } from '../WidgetFormText/Utils/createIMaskInput';
import { useCommonFormUiTranslation } from '../hooks';
import { formats } from './Utils/formats';
import { type } from './types';
const WidgetFormDate = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const format = formats[(_a = props.config.format) !== null && _a !== void 0 ? _a : 'yyyy-mm-dd'];
    const value = (_b = props.internalData.value) !== null && _b !== void 0 ? _b : format.format(props.getDataString());
    const error = getVisibleError(props);
    const inputComponent = React.useMemo(() => { var _a, _b, _c, _d; return createIMaskInput(format.imask((_b = (_a = props.config.mask) === null || _a === void 0 ? void 0 : _a.minYear) !== null && _b !== void 0 ? _b : 1899, (_d = (_c = props.config.mask) === null || _c === void 0 ? void 0 : _c.maxYear) !== null && _d !== void 0 ? _d : 2999)); }, [format, (_c = props.config.mask) === null || _c === void 0 ? void 0 : _c.minYear, (_d = props.config.mask) === null || _d === void 0 ? void 0 : _d.maxYear]);
    const readOnly = props.config.readOnly || ((_e = props.rootParams.formParams) === null || _e === void 0 ? void 0 : _e.readOnly);
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.required, error: !!error, disabled: props.config.disabled },
        React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.required, shrink: true, error: !!error, disabled: props.config.disabled }, elementLabel),
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(OutlinedInput, { id: props.id, label: elementLabel, fullWidth: true, size: 'medium', required: (_h = props.config.validate) === null || _h === void 0 ? void 0 : _h.required, notched: true, error: !!error, value: value, disabled: props.config.disabled, readOnly: readOnly, onChange: (event) => {
                    if (readOnly) {
                        return;
                    }
                    const value = event.target.value;
                    const parsedValue = format.parse(value);
                    if (parsedValue != null) {
                        props.setDataStringOrUndefined(parsedValue);
                        props.internalData.value = value;
                        delete props.internalData.error;
                    }
                    else {
                        props.setDataStringOrUndefined(undefined);
                        props.internalData.value = value;
                    }
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, inputComponent: inputComponent, inputProps: Object.assign(Object.assign({}, (props.editMode ? { tabIndex: -1 } : {})), { 'aria-describedby': ariaDescribedBy }), autoFocus: (_j = (!props.editMode && props.config.autoFocus)) !== null && _j !== void 0 ? _j : false })),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormDateEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('widget.widgetDatePicker.date') + ': ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            readOnly: true,
            autoFocus: true,
            onLoad: (data) => {
                if (!data.format) {
                    data.format = Object.keys(formats)[0];
                }
                return data;
            },
            children: [
                {
                    type: 'form.radio',
                    key: 'format',
                    label: t('common.format'),
                    values: Object.keys(formats).map((format) => ({ value: format, label: format })),
                    validate: {
                        required: true,
                    },
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            uniqueInList: true,
            children: [
                {
                    type: 'form.number',
                    key: 'validate.minAgeYears',
                    label: t('widget.widgetDatePicker.minAgeYears'),
                },
                {
                    type: 'form.text',
                    key: 'validate.minAgeYearsMessage',
                    label: t('widget.widgetDatePicker.minAgeYearsMessage'),
                    emptyAsNull: true,
                },
                {
                    type: 'form.number',
                    key: 'validate.maxAgeYears',
                    label: t('widget.widgetDatePicker.maxAgeYears'),
                },
                {
                    type: 'form.text',
                    key: 'validate.maxAgeYearsMessage',
                    label: t('widget.widgetDatePicker.maxAgeYearsMessage'),
                    emptyAsNull: true,
                },
            ],
        }), buildHiddenTab(props, { t, allowDisable: true }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormDate, Object.assign({}, props))));
};
export const widgetFormDate = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if (value != null) {
            const date = parseISO(value);
            if (isValid(date)) {
                const age = differenceInYears(new Date(), date);
                if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.minAgeYears) != null && age < ((_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.minAgeYears)) {
                    return ((_d = (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.minAgeYearsMessage) !== null && _d !== void 0 ? _d : props.config.label + ' age cannot be less than ' + ((_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.minAgeYears) + ' years');
                }
                if (((_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.maxAgeYears) != null && age > ((_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.maxAgeYears)) {
                    return ((_j = (_h = props.config.validate) === null || _h === void 0 ? void 0 : _h.maxAgeYearsMessage) !== null && _j !== void 0 ? _j : props.config.label + ' age cannot be greater than ' + ((_k = props.config.validate) === null || _k === void 0 ? void 0 : _k.maxAgeYears) + ' years');
                }
            }
            else {
                return 'Invalid date';
            }
        }
        else if (((_l = props.config.validate) === null || _l === void 0 ? void 0 : _l.required) && isValueEmpty(props.internalData.value)) {
            return (_m = props.config.validate.requiredMessage) !== null && _m !== void 0 ? _m : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        else if (!isValueEmpty(props.internalData.value)) {
            return 'Invalid date';
        }
        if (((_o = props.config.validate) === null || _o === void 0 ? void 0 : _o.uniqueInList) || ((_p = props.config.validate) === null || _p === void 0 ? void 0 : _p.uniqueInGrid)) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_q = props.config.validate.uniqueInListMessage) !== null && _q !== void 0 ? _q : error;
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a;
        const value = props.getDataStringOrUndefined();
        if (value) {
            const format = formats[(_a = props.config.format) !== null && _a !== void 0 ? _a : 'yyyy-mm-dd'];
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), format.format(value));
        }
        else {
            return [];
        }
    },
    component: WidgetFormDate,
    icon: React.createElement(CalendarMonthIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormDateEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'date', label: 'Date' }),
};
function isValueEmpty(value) {
    if (!value) {
        return true;
    }
    const str = value.replace(/\D/g, '');
    return str === '';
}
